import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  RCS_ACCEPT_STATUS_ACCEPTED,
  RCS_ACCEPT_STATUS_NONE,
  RCS_CONVERSATION_STATUS_ACTIVE,
  RCS_CONVERSATION_STATUS_PENDING,
} from '@rsos/constants/rcsStatuses';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { useGetRcsConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { RootState } from '../../../../app';
import isTextBasedSource from '../../../../utils/isSourceSMS';
import AllMessages from '../../../PreconfiguredMessages/AllMessages';
import { useEnabledPreconfiguredMessages } from '../../../PreconfiguredMessages/useEnabledPreconfiguredMessages';
import { useChatContext } from '../ChatContext';
import { OutgoingTranslationWarning } from '../SharedChatUI/OutgoingTranslationWarning';
import RcsChat from './RcsChat';
import styles from './RcsChat.module.scss';
import { trackCloseAllMessagesModal } from './RcsChatBoxTrackings';
import RcsChatHeader from './RcsChatHeader';
import { CHAT_HEADER_END, CHAT_HEADER_ACCEPT_DECLINE } from './constants';

const RcsChatBox = () => {
  const {
    isCollapsed,
    isAllMessagesVisible,
    setIsAllMessagesVisible,
    onCopyToChat,
    onSendMessage,
  } = useChatContext();

  const selectedCallerID = useSelector(
    (state: RootState) => state.emergencyCalls.selectedCallerID,
  );

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application || '',
      )?.name,
  );

  const selectedCall = useSelectedCall();
  const mostRecentLocation = getMostRecentLoc(selectedCall);
  const hasRequestLocation = Boolean(
    isTextBasedSource(mostRecentLocation?.source),
  );

  const enabledPreconfiguredMessages = useEnabledPreconfiguredMessages(
    orgName,
    { hasRequestLocation },
  );

  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  useGetRcsConversationMetadataQuery(
    {
      orgName,
      callerID: selectedCallerID,
    },
    {
      skip: !selectedCallerID || rcsConversation,
    },
  );

  const handleViewClose = () => {
    setIsAllMessagesVisible(false);
    trackCloseAllMessagesModal(rcsConversation?.data?.conversation_id);
  };

  const showButtons = useMemo(() => {
    const STATUS_TO_BUTTON_MAP: Record<string, string> = {
      [`${RCS_ACCEPT_STATUS_NONE}${RCS_CONVERSATION_STATUS_PENDING}`]:
        CHAT_HEADER_ACCEPT_DECLINE,
      [`${RCS_ACCEPT_STATUS_ACCEPTED}${RCS_CONVERSATION_STATUS_ACTIVE}`]:
        CHAT_HEADER_END,
    };

    if (!rcsConversation || !rcsConversation.data) {
      return null;
    }
    const { accept_status, conversation_status } = rcsConversation.data;
    return (
      STATUS_TO_BUTTON_MAP[`${accept_status}${conversation_status}`] ?? null
    );
  }, [rcsConversation]);

  if (!selectedCallerID) {
    return null;
  }

  return (
    <div
      className={`${styles.rcsChatContainer} ${isCollapsed ? styles.collapsed : styles.expanded}`}
      data-name="rcs-chat-container"
    >
      <RcsChatHeader showButtons={showButtons} />
      <RcsChat
        callerID={selectedCallerID}
        isRcsChatBoxVisible={!isCollapsed}
        preconfiguredMessages={enabledPreconfiguredMessages}
        hasRequestLocationAccess={hasRequestLocation}
      />
      {!isCollapsed && showButtons && <OutgoingTranslationWarning />}
      {isAllMessagesVisible && (
        <div className={styles.absoluteAllMessagesContainer}>
          <AllMessages
            preconfiguredMessages={enabledPreconfiguredMessages}
            onClose={handleViewClose}
            onCopyToChat={onCopyToChat}
            onSendMessage={onSendMessage}
          />
        </div>
      )}
    </div>
  );
};

export default RcsChatBox;
